/**
 * Chat API Client
 * Handles all chat session and message persistence operations
 */
import { apiClient } from './client';

export interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface ChatSession {
  id: number;
  session_id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
  message_count?: number;
}

export interface CreateSessionRequest {
  title?: string;
}

export interface CreateMessageRequest {
  role: 'user' | 'assistant';
  content: string;
  metadata?: Record<string, any>;
}

/**
 * Create a new chat session
 */
export const createChatSession = async (data: CreateSessionRequest = {}): Promise<ChatSession> => {
  const response = await apiClient.post<ChatSession>('/api/chat/sessions', data);
  return response.data;
};

/**
 * Get all chat sessions for the current user
 */
export const getChatSessions = async (): Promise<ChatSession[]> => {
  const response = await apiClient.get<ChatSession[]>('/api/chat/sessions');
  return response.data;
};

/**
 * Get all messages for a specific session
 */
export const getSessionMessages = async (sessionId: string): Promise<ChatMessage[]> => {
  const response = await apiClient.get<ChatMessage[]>(`/api/chat/sessions/${sessionId}/messages`);
  return response.data;
};

/**
 * Add a new message to a session
 */
export const addMessage = async (
  sessionId: string,
  message: CreateMessageRequest
): Promise<ChatMessage> => {
  const response = await apiClient.post<ChatMessage>(
    `/api/chat/sessions/${sessionId}/messages`,
    message
  );
  return response.data;
};

/**
 * Delete a chat session
 */
export const deleteChatSession = async (sessionId: string): Promise<void> => {
  await apiClient.delete(`/api/chat/sessions/${sessionId}`);
};

/**
 * Update a session's title
 */
export const updateSessionTitle = async (sessionId: string, title: string): Promise<void> => {
  await apiClient.patch(`/api/chat/sessions/${sessionId}/title`, { title });
};
